/**
 * OpenWells Export — build the OpenWells import XML for the current well over a
 * date window. Talks to the live Oxbo-P export endpoint:
 *
 *   GET ${VITE_OXBO_P_URL}/v1/export/openwells
 *       ?well_uwi=&date_from=&date_to=&format=edm_xml|witsml
 *
 * The server returns `application/xml` as a download (Content-Disposition
 * attachment). Because the endpoint is JWT-authed we cannot use a plain
 * `<a href>` — the browser wouldn't attach the bearer token. So we `fetch` with
 * the same Authorization header the RTK baseQuery uses (`getBrokerToken()`),
 * read the body as a Blob, and trigger a download from an object URL, honoring
 * the server's filename (Content-Disposition) with a sensible fallback.
 *
 * "Changes between X and Y" = lineage timestamps in [date_from, date_to].
 * Covered objects: well / wellbore / event / daily / activities / cost. Others
 * (tubulars/BHA, mud, surveys) are deferred and called out in the UI.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocale } from "@hooks/useLocale";
import { getBrokerToken, isGlobalAdmin } from "@api/brokerAuth";
import { useGetEndOfWellQuery } from "@features/analytics/analyticsApi";

const P_BASE_URL = import.meta.env.VITE_OXBO_P_URL ?? "https://p.dev.oxbo.energy";

/** Header the server sets to list tables it capped (truncated export). */
const TRUNCATED_HEADER = "X-Oxbo-Export-Truncated";

export type ExportFormat = "edm_xml" | "witsml";

export interface OpenWellsExportProps {
  wellUwi: string;
}

type Feedback =
  | { kind: "error"; message: string }
  | { kind: "warn"; message: string }
  | { kind: "success"; message: string };

/** Pull the download filename out of a Content-Disposition header, if present. */
function filenameFromDisposition(disposition: string | null): string | null {
  if (!disposition) return null;
  // RFC 5987 `filename*=UTF-8''name` first, then a plain quoted/bare `filename=`.
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(disposition);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ""));
    } catch {
      /* fall through to the plain form */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain?.[1]?.trim() ?? null;
}

/** Fallback download name when the server sends no Content-Disposition. */
function fallbackFilename(wellUwi: string, from: string, to: string, format: ExportFormat): string {
  const safeWell = wellUwi.replace(/[^\w.-]+/g, "_") || "well";
  return `oxbo_p_${safeWell}_${from}_${to}.${format}.xml`;
}

export function OpenWellsExport({ wellUwi }: OpenWellsExportProps) {
  const { t } = useLocale();
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [format, setFormat] = useState<ExportFormat>("edm_xml");
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  // Default the date window to the well's data extent (spud → last report), and
  // RE-default whenever the selected well changes (once per well) — otherwise the
  // previous well's dates linger and a different well's data falls outside them.
  const { data: extent } = useGetEndOfWellQuery(wellUwi, { skip: !wellUwi });
  const defaultedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!extent || !wellUwi || defaultedFor.current === wellUwi) return;
    const spud = (extent.spud_date ?? "").slice(0, 10);
    const last = (extent.last_report_date ?? "").slice(0, 10);
    if (spud) setDateFrom(spud);
    if (last) setDateTo(last);
    defaultedFor.current = wellUwi;
  }, [extent, wellUwi]);

  // Client-side guard mirrors the server's 422 so we never fire a doomed request.
  const dateRangeInvalid = useMemo(
    () => dateFrom !== "" && dateTo !== "" && dateFrom > dateTo,
    [dateFrom, dateTo],
  );
  const canSubmit = wellUwi !== "" && !busy && !dateRangeInvalid;

  async function handleSubmit() {
    setFeedback(null);
    if (dateRangeInvalid) {
      setFeedback({ kind: "error", message: t("export.error.dateRange") });
      return;
    }
    setBusy(true);
    try {
      const params = new URLSearchParams({ well_uwi: wellUwi, format });
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);

      const headers: Record<string, string> = {};
      const token = getBrokerToken();
      if (token) headers.Authorization = `Bearer ${token}`;
      // Cross-tenant admins (e.g. @roderickc.com, org=RCI) must opt into admin-mode
      // or RLS scopes the export to their own org and returns only the well stub.
      if (isGlobalAdmin()) headers["X-OXBO-Admin-Mode"] = "true";

      const res = await fetch(`${P_BASE_URL}/v1/export/openwells?${params.toString()}`, {
        method: "GET",
        headers,
      });

      if (res.status === 422) {
        setFeedback({ kind: "error", message: t("export.error.dateRange") });
        return;
      }
      if (res.status === 401 || res.status === 403) {
        setFeedback({ kind: "error", message: t("export.error.auth") });
        return;
      }
      if (!res.ok) {
        setFeedback({ kind: "error", message: t("export.error.generic") });
        return;
      }

      const truncated = res.headers.get(TRUNCATED_HEADER);
      const blob = await res.blob();
      const filename =
        filenameFromDisposition(res.headers.get("Content-Disposition")) ??
        fallbackFilename(wellUwi, dateFrom || "all", dateTo || "all", format);

      // Object-URL download: attach a throwaway anchor, click, then revoke.
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      if (truncated) {
        setFeedback({ kind: "warn", message: t("export.warn.truncated", { tables: truncated }) });
      } else {
        setFeedback({ kind: "success", message: t("export.success") });
      }
    } catch {
      setFeedback({ kind: "error", message: t("export.error.generic") });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="page-head">
        <h1>{t("export.title")}</h1>
        <p className="subtitle">{t("export.blurb")}</p>
      </div>

      <div className="card" style={{ marginTop: 14 }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 14,
            alignItems: "flex-end",
          }}
        >
          <div className="field">
            <label htmlFor="export-well">{t("export.well")}</label>
            <input id="export-well" type="text" value={wellUwi} readOnly />
          </div>

          <div className="field">
            <label htmlFor="export-from">{t("export.from")}</label>
            <input
              id="export-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="export-to">{t("export.to")}</label>
            <input
              id="export-to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="export-format">{t("export.format")}</label>
            <select
              id="export-format"
              value={format}
              onChange={(e) => setFormat(e.target.value as ExportFormat)}
            >
              <option value="edm_xml">{t("export.format.edmXml")}</option>
              <option value="witsml">{t("export.format.witsml")}</option>
            </select>
          </div>

          <div className="field">
            <button
              type="button"
              className="btn primary"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {busy ? t("export.generating") : t("export.generate")}
            </button>
          </div>
        </div>

        {dateRangeInvalid ? (
          <p role="alert" style={{ marginTop: 12, color: "var(--danger, #d64545)" }}>
            {t("export.error.dateRange")}
          </p>
        ) : null}

        {feedback ? (
          <p
            role={feedback.kind === "error" ? "alert" : "status"}
            style={{
              marginTop: 12,
              color:
                feedback.kind === "error"
                  ? "var(--danger, #d64545)"
                  : feedback.kind === "warn"
                    ? "var(--warn, #b8860b)"
                    : "var(--dim)",
            }}
          >
            {feedback.message}
          </p>
        ) : null}

        <p className="subtitle" style={{ marginTop: 16 }}>
          {t("export.deferredNote")}
        </p>
      </div>
    </div>
  );
}
